import type { ColumnDef } from "@tanstack/react-table";
import {
  Activity as ActivityIcon,
  Calendar,
  Hash,
  Star,
  Users,
  Users2,
} from "lucide-react";
import { useMemo } from "react";
import type { Activity } from "@/hooks/api/use-account-activities";
import type { CollapsedActivity } from "@/pages/accounts/account-explorer/touchpoints-table/types";
import type { UnifiedTableColumn } from "@/types/table-column";

export interface FilterOption {
  id: string;
  label: string;
}

/**
 * Get column definitions for the activity table
 */
export function getActivityColumns(filterOptions?: {
  channels?: FilterOption[];
  teams?: FilterOption[];
  people?: FilterOption[];
  statuses?: FilterOption[];
  direction?: FilterOption[];
}): UnifiedTableColumn<Activity | CollapsedActivity>[] {
  return [
    {
      key: "gutter",
      title: "",
      size: 20, // Fixed
    },
    {
      key: "direction",
      title: "Direction",
      size: 25, // Fixed
      filterable: true,
      filterType: "select",
      filterOptions: filterOptions?.direction || [],
    },
    {
      key: "expand",
      title: "",
      size: 30, // Fixed
    },
    {
      key: "date",
      title: "Date",
      size: 120, // Fixed
      icon: <Calendar className="h-4 w-4" />,
      accessorKey: "date",
    },
    {
      key: "activity",
      title: "Activity",
      icon: <ActivityIcon className="h-4 w-4" />,
      accessorKey: "activity",
      filterable: true,
      filterType: "text",
      size: 300, // Minimum width - this column will auto-expand to fill remaining space
    },
    {
      key: "people",
      title: "People",
      icon: <Users className="h-4 w-4" />,
      accessorKey: "people",
      filterable: true,
      filterType: "select",
      filterOptions: filterOptions?.people || [],
      filterWidth: 400,
      filterMaxHeight: 350,
      size: 180, // Fixed
    },
    {
      key: "channel",
      title: "Channel",
      icon: <Hash className="h-4 w-4" />,
      accessorKey: "channel",
      filterable: true,
      filterType: "select",
      filterOptions: filterOptions?.channels || [],
      size: 120, // Fixed
    },
    {
      key: "status",
      title: "Status",
      icon: <Star className="h-4 w-4" />,
      accessorKey: "status",
      filterable: true,
      filterType: "select",
      filterOptions: filterOptions?.statuses || [],
      size: 100, // Fixed
    },
    {
      key: "team",
      title: "Team",
      icon: <Users2 className="h-4 w-4" />,
      accessorKey: "team",
      filterable: true,
      filterType: "select",
      filterOptions: filterOptions?.teams || [],
      size: 140, // Fixed
    },
  ];
}

/**
 * Custom hook to convert activity columns to tanstack column definitions
 */
export function useActivityTableColumns(filterOptions?: {
  channels?: FilterOption[];
  teams?: FilterOption[];
  people?: FilterOption[];
  statuses?: FilterOption[];
}): ColumnDef<Activity | CollapsedActivity>[] {
  // Get the default columns based on filter options
  const activityColumns = useMemo(
    () => getActivityColumns(filterOptions),
    [filterOptions],
  );

  // Convert the activity columns to tanstack table columns
  return useMemo(
    () =>
      activityColumns.map((column) => ({
        id: column.key,
        accessorKey: column.accessorKey,
        size: column.size,
        header: () =>
          column.title ? (
            <div className="flex items-center gap-2 text-xs font-normal text-muted-foreground">
              {column.icon}
              {column.title}
            </div>
          ) : null,
      })),
    [activityColumns],
  );
}
